"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertCircle } from "lucide-react";
import type { AgentRagSettings } from "@/lib/agents/shared/types";
import { RAG_DEFAULTS } from "@/lib/ai/knowledge-base/constants";
export type { AgentRagSettings };

interface AgentRagSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasRagDocuments: boolean;
  currentSettings?: AgentRagSettings;
  onSave: (settings: AgentRagSettings) => Promise<boolean>;
}

const DEFAULT_RAG_LIMIT = RAG_DEFAULTS.limit;
const DEFAULT_RAG_THRESHOLD = RAG_DEFAULTS.threshold;

export function AgentRagSettingsDialog({
  open,
  onOpenChange,
  hasRagDocuments,
  currentSettings,
  onSave,
}: AgentRagSettingsDialogProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [ragLimit, setRagLimit] = useState(
    currentSettings?.rag_limit ?? DEFAULT_RAG_LIMIT
  );
  const [ragThreshold, setRagThreshold] = useState(
    currentSettings?.rag_threshold ?? DEFAULT_RAG_THRESHOLD
  );

  useEffect(() => {
    if (open) {
      setRagLimit(currentSettings?.rag_limit ?? DEFAULT_RAG_LIMIT);
      setRagThreshold(currentSettings?.rag_threshold ?? DEFAULT_RAG_THRESHOLD);
      setError(null);
    }
  }, [open, currentSettings]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const success = await onSave({
        rag_limit: ragLimit,
        rag_threshold: ragThreshold,
      });

      if (success) {
        onOpenChange(false);
      } else {
        setError("Failed to save RAG settings");
      }
    } catch (err) {
      console.error("Error saving RAG settings:", err);
      setError("Failed to save RAG settings");
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    ragLimit !== (currentSettings?.rag_limit ?? DEFAULT_RAG_LIMIT) ||
    ragThreshold !== (currentSettings?.rag_threshold ?? DEFAULT_RAG_THRESHOLD);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>RAG Settings</DialogTitle>
          <DialogDescription>
            Configure retrieval parameters for knowledge base search.
            These settings override the defaults from the use case config.
          </DialogDescription>
        </DialogHeader>

        {!hasRagDocuments ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No RAG knowledge bases are connected to this agent. Add a knowledge base with RAG mode to configure these settings.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-6 py-4">
            {/* RAG Limit */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="rag-limit" className="text-sm font-medium">
                  Max Chunks
                </Label>
                <span className="text-sm font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  {ragLimit}
                </span>
              </div>
              <Slider
                id="rag-limit"
                min={1}
                max={20}
                step={1}
                value={[ragLimit]}
                onValueChange={(value) => setRagLimit(value[0])}
                disabled={isSaving}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                Maximum number of document chunks to retrieve (1-20). Higher values provide more context but increase token usage.
              </p>
            </div>

            {/* RAG Threshold */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="rag-threshold" className="text-sm font-medium">
                  Similarity Threshold
                </Label>
                <span className="text-sm font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  {ragThreshold.toFixed(2)}
                </span>
              </div>
              <Slider
                id="rag-threshold"
                min={0.01}
                max={0.99}
                step={0.01}
                value={[ragThreshold]}
                onValueChange={(value) => setRagThreshold(value[0])}
                disabled={isSaving}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                Minimum similarity score for search results (0.01-0.99). Lower values return more results but may be less relevant.
              </p>
            </div>

            {error && (
              <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                <AlertDescription className="text-red-800 dark:text-red-200">
                  {error}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          {hasRagDocuments && (
            <Button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Apply Changes"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
